/**
 * [FROST BUDDY] Shared runtime preference for the sibling + variant picker
 * mobile layout. The merchant sets a default per block (vertical | horizontal),
 * and the shopper can flip it via the segmented dual-icon toggle next to the
 * picker header. The preference is shared across both pickers on a page and
 * persists across reloads in localStorage.
 *
 * The class dispatches a `fb:picker-layout-change` CustomEvent on document
 * whenever the preference changes, so each picker can react without polling.
 */
class PickerLayout {
  static #STORAGE_KEY = 'fb:picker-layout';
  static #EVENT_NAME = 'fb:picker-layout-change';

  /** @returns {'vertical' | 'horizontal' | null} */
  static get() {
    try {
      const value = localStorage.getItem(PickerLayout.#STORAGE_KEY);
      if (value === 'vertical' || value === 'horizontal') return value;
      return null;
    } catch (_) {
      return null;
    }
  }

  /** @param {'vertical' | 'horizontal'} value */
  static set(value) {
    if (value !== 'vertical' && value !== 'horizontal') return;
    try { localStorage.setItem(PickerLayout.#STORAGE_KEY, value); } catch (_) {}
    document.dispatchEvent(new CustomEvent(PickerLayout.#EVENT_NAME, {
      detail: { value },
    }));
  }

  /** @param {(event: CustomEvent<{ value: 'vertical' | 'horizontal' }>) => void} handler */
  static onChange(handler) {
    document.addEventListener(PickerLayout.#EVENT_NAME, handler);
  }
}

window.PickerLayout = PickerLayout;

// [FROST BUDDY] Sibling-picker "See all N designs" expand, handled via a single
// document-level delegated listener attached here instead of a per-node listener
// bound in the custom element's connectedCallback.
//
// Why this lives here (not in sibling-picker.js): sibling-picker.js loads as a
// low-priority ES module, so on some loads a shopper clicks the already-painted
// "See all" button before that module has upgraded the element and bound its
// listener — the click was silently dropped ("button sometimes doesn't work",
// mostly on desktop where it's the primary control). This file loads `defer` in
// <head>, so the listener is live by end-of-parse, well before the mid-page
// picker is interacted with. Delegation also means it keeps working after an
// AJAX sibling swap (morph) and for any number of pickers on the page.
//
// `data-expanded` on the panel stays the single source of truth; the CSS drops
// the collapsed max-height/mask off .sibling-picker__grid. The other paths that
// set it (bucket filter, layout toggle, post-swap restore) are unaffected.
document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const button = target.closest('[data-grid-expand]');
  if (!button) return;
  const panel = button.closest('.sibling-picker__panel');
  if (!panel) return;
  panel.setAttribute('data-expanded', 'true');
  button.setAttribute('aria-expanded', 'true');
});
